import { identity, unwrap } from "../api";

/**
 * `GET|PUT /api/identity/matrix` (`MatrixController.cs`) — the role-permission matrix's
 * administrative surface. `apps/admin` had no screen for it before this file; `pages/role-matrix`
 * is the first caller.
 *
 * Identity declares no response `content` on either operation (backend-gaps GAP-4), so the body is
 * parsed at runtime rather than assumed, same discipline as `parseAccount`/`parsePermissions` in
 * `lib/identity.ts`. `roles` maps a role name to the permission codes granted to it — free-form
 * strings (`IMatrixStore.cs`'s own note: "Names, never ids"), not this app's own `Permission` union,
 * which is why this file does not import `types/index.ts`'s `Permission` type.
 */
export type MatrixDocument = {
  version: number;
  roles: Record<string, string[]>;
};

function parseMatrixDocument(body: unknown): MatrixDocument {
  const raw = (body ?? {}) as Record<string, unknown>;
  const version = typeof raw.version === "number" ? raw.version : null;
  const rolesRaw = raw.roles;

  if (version === null || typeof rolesRaw !== "object" || rolesRaw === null) {
    throw new Error(
      "GET /api/identity/matrix answered without a numeric version and a roles object. " +
        "The editor has nothing to display.",
    );
  }

  const roles: Record<string, string[]> = {};
  for (const [role, permissions] of Object.entries(rolesRaw as Record<string, unknown>)) {
    if (!Array.isArray(permissions) || !permissions.every((p): p is string => typeof p === "string")) {
      throw new Error(`GET /api/identity/matrix's role '${role}' did not carry a string permission array.`);
    }
    roles[role] = permissions;
  }

  return { version, roles };
}

/** The matrix as it stands, for the editor to display (`GetMatrixQuery`, Super Admin only). */
export async function getMatrix(): Promise<MatrixDocument> {
  return parseMatrixDocument(await unwrap((await identity()).GET("/api/identity/matrix")));
}

/**
 * Replaces the whole matrix (`UpdateMatrixHandler`). A document the store cannot use comes back as
 * a 422 naming what is wrong with it (`MatrixController.cs`'s own `Problem(...)`) — the caller
 * renders `err.detail` rather than a generic failure, same as every other write in this app.
 */
export async function putMatrix(document: MatrixDocument): Promise<void> {
  await unwrap((await identity()).PUT("/api/identity/matrix", { body: document }));
}
